/**
 * Rendered article HTML — Step 33, revised twice from Kai's reports.
 *
 * First attempt rendered on a permanently white "sheet of paper" to protect
 * the authored colours. That worked but looked wrong: "the whole background
 * is still white, but it doesn't match the admin end". So the surface is now
 * the app's own themed card, and the CONTENT is adapted to the theme instead
 * — see articleTheme.ts for why that beats both stripping colours and
 * keeping a white slab.
 *
 * Light mode renders the HTML byte-for-byte as authored. Only dark mode
 * rewrites, and only the parts that assumed a white page.
 */
import { useEffect, useMemo, useState } from 'react';
import { adaptArticleHtml } from './articleTheme';
import { cn } from '../../lib/utils';

/** The theme is a class on <html>, toggled by the top bar and restored from
 *  localStorage on boot. Observing it means an article re-adapts the moment
 *  the toggle is hit, rather than on the next navigation. */
function useIsDark(): boolean {
  const [dark, setDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );
  useEffect(() => {
    const el = document.documentElement;
    const observer = new MutationObserver(() => setDark(el.classList.contains('dark')));
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

export function ArticleBody({ html, className }: { html: string | null; className?: string }) {
  const dark = useIsDark();
  const adapted = useMemo(() => adaptArticleHtml(html ?? '', dark), [html, dark]);

  if (!html || !html.trim()) {
    return <p className="text-sm text-muted-foreground">This article has no content yet.</p>;
  }
  return (
    <div
      className={cn('kb-article max-w-none overflow-x-auto text-foreground', className)}
      // Sanitised on WRITE via lib/sanitize.ts; adapted for the theme above,
      // which only edits already-allowlisted style declarations.
      dangerouslySetInnerHTML={{ __html: adapted }}
    />
  );
}
